import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useToast,
  Spinner,
  Box,
  Text,
  Icon,
  Flex,
  Spacer,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay
} from '@chakra-ui/react';
import { PhoneIcon } from '@chakra-ui/icons';
import api from '../../../../services/api';
import { useSocket } from '../../../../contexts/SocketContext';
import { NewKBLeadFormProvider, useNewKBLeadForm } from '../context/NewKBLeadFormContext';
import { validateFormData, transformFormDataForSubmission } from '../utils/leadFormUtils';
import NewKBBasicInfoForm from './NewKBBasicInfoForm';
import NewKBEventForm from './NewKBEventForm';
import NewKBPreferencesForm from './NewKBPreferencesForm';
import NewKBVenueForm from './NewKBVenueForm';
import NewKBProposalForm from './NewKBProposalForm';
import NewKBBudgetForm from './NewKBBudgetForm';
import NewKBCallsTab from './NewKBCallsTab';
import NewKBCommentsForm from './NewKBCommentsForm';

/**
 * NewKBLeadModal - A modal component for creating new leads or editing existing ones
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Controls whether the modal is visible
 * @param {Function} props.onClose - Function to call when the modal is closed
 * @param {string} props.leadId - (Optional) ID of existing lead to edit
 * @param {Function} props.onLeadAdded - Callback function when a lead is created or updated
 */
const NewKBLeadModal = ({ isOpen, onClose, leadId, onLeadAdded }) => {
  // Component state
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [initialFormData, setInitialFormData] = useState(null);
  
  // Delete confirmation dialog
  const { isOpen: isDeleteDialogOpen, onOpen: openDeleteDialog, onClose: closeDeleteDialog } = useDisclosure();
  const cancelRef = useRef();
  
  // Reference data state
  const [metadata, setMetadata] = useState({
    eventTypes: [],
    leadSources: [],
    stages: [],
    users: [],
    entertainmentOptions: []
  });
  
  // Item counts state
  const [counts, setCounts] = useState({
    commentCount: 0,
    taskCount: 0,
    callCount: 0
  });
  
  const { socket } = useSocket();
  const toast = useToast();
  const isEditMode = !!leadId;
  
  // Fetch constants and metadata when component mounts
  useEffect(() => {
    fetchConstants();
    fetchUsers();
  }, []);
  
  // Fetch lead data when in edit mode and leadId changes
  useEffect(() => {
    if (isEditMode && leadId) {
      fetchLeadDetails(leadId);
    } else {
      // Reset to initial state when not in edit mode
      setInitialFormData(null);
    }
  }, [leadId, isEditMode]);
  
  // Fetch reference constants
  const fetchConstants = async () => {
    try {
      const [eventTypesRes, leadSourcesRes, stagesRes, entertainmentOptionsRes] = await Promise.all([
        api.get('/api/constants/EVENT_TYPES'),
        api.get('/api/constants/LEAD_SOURCES'),
        api.get('/api/constants/STAGES'),
        api.get('/api/constants/ENTERTAINMENT_OPTIONS')
      ]);
      
      const newMetadata = {
        eventTypes: eventTypesRes.data.values,
        leadSources: leadSourcesRes.data.values,
        stages: stagesRes.data.values,
        entertainmentOptions: entertainmentOptionsRes.data.values
      };
      
      setMetadata(prev => ({
        ...prev,
        ...newMetadata
      }));
      
      console.log('[NewKBLeadModal] Fetched constants:', {
        eventTypesCount: eventTypesRes.data.values.length,
        eventTypesSample: eventTypesRes.data.values.slice(0, 2),
        leadSourcesCount: leadSourcesRes.data.values.length,
        stagesCount: stagesRes.data.values.length
      });
    } catch (error) {
      console.error('[NewKBLeadModal] Error fetching constants:', error);
      toast({
        title: 'Error fetching reference data',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 5000
      });
    }
  };
  
  // Fetch users
  const fetchUsers = async () => {
    try {
      const response = await api.get('/api/users');
      setMetadata(prev => ({ ...prev, users: response.data.users }));
    } catch (error) {
      console.error('[NewKBLeadModal] Error fetching users:', error);
    }
  };
  
  // Fetch detailed lead data
  const fetchLeadDetails = async (id) => {
    setIsFetching(true);
    try {
      const response = await api.get(`/api/leads/${id}`);
      const lead = response.data;
      console.log('[NewKBLeadModal] Fetched lead details:', lead);
      
      // Transform lead data for form
      transformLeadDataForForm(lead);
      
      // Fetch associated counts
      fetchCounts(id);
    } catch (error) {
      console.error('[NewKBLeadModal] Error fetching lead details:', error);
      toast({
        title: 'Error fetching lead',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 5000
      });
    } finally {
      setIsFetching(false);
    }
  };
  
  // Fetch counts for comments, tasks, and calls
  const fetchCounts = async (id) => {
    try {
      const [commentRes, taskRes, callRes] = await Promise.all([
        api.get(`/api/comments/lead/${id}`),
        api.get(`/api/tasks/lead/${id}`),
        api.get(`/api/call-logs/lead/${id}`)
      ]);
      
      setCounts({
        commentCount: commentRes.data.total || 0,
        taskCount: taskRes.data.total || 0,
        callCount: callRes.data.total || 0
      });
    } catch (error) {
      console.error('[NewKBLeadModal] Error fetching counts:', error);
    }
  };
  
  // Transform API lead data for form consumption
  const transformLeadDataForForm = (lead) => {
    // Transform lead data for form
    const transformedLead = {
      ...lead,
      // Ensure required objects exist
      stage: lead.stage || { _id: 'stage_lead_in' },
      leadSource: lead.leadSource || null,
      locations: lead.locations || { cities: [], venues: [] },
      budget: lead.budget || { amount: null, currency: 'INR', notes: '' },
      proposal: lead.proposal || { status: 'pending', details: {} },
      // Map events to use event type IDs
      events: (lead.events || []).map(event => {
        console.log('[transformLeadDataForForm] Processing event:', event);
        
        // Handle MongoDB document format - extract data from _doc if it exists
        const eventData = event._doc || event;
        
        // Handle the case where type is an object with _id
        let eventTypeId = '';
        
        if (eventData.type) {
          // If type is already an object with _id, use that _id
          if (typeof eventData.type === 'object' && eventData.type._id) {
            eventTypeId = eventData.type._id;
            console.log('[transformLeadDataForForm] Found event type as object:', { 
              typeId: eventTypeId, 
              typeName: eventData.type.name 
            });
          } else {
            // If type is a string, use it directly
            eventTypeId = eventData.type;
            console.log('[transformLeadDataForForm] Found event type as string:', eventTypeId);
          }
        }
        
        return {
          ...eventData,
          type: eventTypeId,
          // Ensure other fields are present
          guestCount: eventData.guestCount || null,
          startTime: eventData.startTime || '',
          endTime: eventData.endTime || '',
          notes: eventData.notes || ''
        };
      }) || [{
        type: '',
        guestCount: null,
        startTime: '',
        endTime: '',
        notes: ''
      }],
      eventDates: lead.eventDates || [],
      
      // Transform entertainment options to array of IDs
      entertainmentOptions: (lead.entertainmentOptions || []).map(option => {
        // If it's already an object with _id, extract the ID
        if (typeof option === 'object' && option._id) {
          console.log('[transformLeadDataForForm] Extracting entertainment option ID:', option._id);
          return option._id;
        }
        // If it's already a string ID, return it
        return option;
      })
    };
    
    // Set as initial form data for the context
    setInitialFormData(transformedLead);
  };
  
  // Handle form submission with context data
  const handleSubmit = async (formData) => {
    if (!formData) return;
    
    setIsLoading(true);
    try {
      // Validate form data
      const validation = validateFormData(formData);
      if (!validation.isValid) {
        // Show validation errors
        toast({
          title: 'Validation Error',
          description: validation.errors.join('\n'),
          status: 'error',
          duration: 5000
        });
        setIsLoading(false);
        return;
      }
      
      // Format data for API
      console.log('[NewKBLeadModal] Form data before submission:', {
        events: formData.events,
        metadataEventTypes: metadata.eventTypes,
      });
      
      const submitData = transformFormDataForSubmission(formData, metadata);
      console.log('[NewKBLeadModal] Submitting lead data:', {
        events: submitData.events,
        originalEvents: formData.events
      });
      
      // Make API call based on mode
      const response = isEditMode
        ? await api.put(`/api/leads/${leadId}`, submitData)
        : await api.post('/api/leads', submitData);
      
      // Emit socket event for real-time updates
      if (socket) {
        const eventName = isEditMode ? 'lead:updated' : 'lead:created';
        const businessLeadId = response.data.leadId || leadId;
        
        // Create a clean lead object with no circular references
        const cleanLeadData = JSON.parse(JSON.stringify(response.data));
        
        // Add timestamp to force reactivity
        cleanLeadData._socketUpdateTime = Date.now();
        
        // Emit with properly structured data
        const eventData = {
          leadId: businessLeadId,
          lead: cleanLeadData,
          // Include key fields directly at top level for backwards compatibility
          brideAndGroomName: cleanLeadData.brideAndGroomName,
          stage: typeof cleanLeadData.stage === 'object' ? cleanLeadData.stage._id : cleanLeadData.stage,
          nextFollowUpTime: cleanLeadData.nextFollowUpTime,
          // Add timestamp to help debug event sequence
          emittedAt: new Date().toISOString()
        };
        
        socket.emit(eventName, eventData);
        console.log(`[SOCKET] Emitted ${eventName}`, { 
          leadId: businessLeadId,
          name: cleanLeadData.brideAndGroomName,
          timestamp: new Date().toLocaleTimeString()
        });
      }
      
      toast({
        title: isEditMode ? 'Lead Updated' : 'Lead Created',
        description: isEditMode ? 'Lead has been updated successfully' : 'New lead has been created',
        status: 'success',
        duration: 5000
      });
      
      // Call the onLeadAdded callback with the response data
      onLeadAdded(response.data);
      
      // Only close the modal when creating a new lead, not when updating
      if (!isEditMode) {
        onClose();
      } else {
        // For edit mode, keep the modal open but show success message
        // We need to properly transform the API response data before setting it as form data
        // This ensures events and other complex fields are properly formatted
        // Note: we're re-fetching the lead to get the complete data structure
        fetchLeadDetails(leadId);
        
        // Reset to the active tab that the user was on
        // This helps maintain context when making multiple edits
        setActiveTab(activeTab);
      }
    } catch (error) {
      console.error('[NewKBLeadModal] Error saving lead:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading indicator during fetch
  if (isEditMode && isFetching) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Loading Lead Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box display="flex" justifyContent="center" alignItems="center" py={10}>
              <Spinner size="xl" color="blue.500" mr={4} />
              <Text>Loading lead data...</Text>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  // Delete lead function
  const deleteLead = async () => {
    if (isDeleting || !leadId) return;
    
    setIsDeleting(true);
    try {
      await api.delete(`/api/leads/${leadId}`);
      
      toast({
        title: 'Lead Deleted',
        description: 'Lead has been successfully deleted',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
      
      // Emit socket event for real-time updates
      if (socket) {
        socket.emit('lead:deleted', { leadId });
        console.log(`[SOCKET] Emitted lead:deleted`, { 
          leadId: leadId,
          timestamp: new Date().toLocaleTimeString()
        });
      }
      
      if (onLeadAdded) {
        onLeadAdded(null); // Trigger refresh
      }
      
      onClose();
    } catch (error) {
      console.error('[NewKBLeadModal] Error deleting lead:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to delete lead',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsDeleting(false);
      closeDeleteDialog();
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent maxW="900px">
          <ModalHeader>
            <Flex width="100%" align="center">
              <Text>{isEditMode ? 'Edit Lead' : 'Add New Lead'}</Text>
              <Spacer />
              {isEditMode && (
                <Button
                  size="sm"
                  colorScheme="red"
                  variant="outline"
                  onClick={openDeleteDialog}
                  mr={10} // Add more right margin to keep distance from close button
                >
                  Delete Lead
                </Button>
              )}
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          
          {/* Wrap form content in the provider */}
          <NewKBLeadFormProvider initialData={initialFormData} metadata={metadata}>
            {/* This component will consume context via useNewKBLeadForm() hook */}
            <NewKBLeadModalContent 
              isLoading={isLoading}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              onClose={onClose}
              isEditMode={isEditMode}
              onSubmit={handleSubmit}
              counts={counts}
            />
          </NewKBLeadFormProvider>
        </ModalContent>
      </Modal>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={closeDeleteDialog}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Lead
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this lead? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeDeleteDialog}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={deleteLead} ml={3} isLoading={isDeleting}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

/**
 * Internal component that consumes the form context
 */
const NewKBLeadModalContent = ({ 
  isLoading, 
  activeTab, 
  setActiveTab, 
  onClose, 
  isEditMode, 
  onSubmit,
  counts
}) => {
  const toast = useToast();
  // Get form context data and methods
  const { 
    formData, 
    isFormDirty, 
    validationErrors, 
    metadata,
    // ...other context values
  } = useNewKBLeadForm();
  
  // Handle form submission  
  const handleSubmit = () => {
    onSubmit(formData);
  };
  
  return (
    <>
      <ModalBody>
        <Tabs index={activeTab} onChange={setActiveTab}>
          <TabList>
            <Tab>Basic Info</Tab>
            <Tab>Events</Tab>
            <Tab>Venue</Tab>
            <Tab>Budget</Tab>
            <Tab>Preferences</Tab>
            <Tab>Proposal</Tab>
            <Tab>Calls</Tab>
            <Tab>Comments</Tab>
            {/* Additional tabs can be added here */}
          </TabList>
          
          <TabPanels>
            {/* Form components */}
            <TabPanel>
              <NewKBBasicInfoForm />
            </TabPanel>
            <TabPanel>
              <NewKBEventForm />
            </TabPanel>
            <TabPanel>
              <NewKBVenueForm />
            </TabPanel>
            <TabPanel>
              <NewKBBudgetForm />
            </TabPanel>
            <TabPanel>
              <NewKBPreferencesForm />
            </TabPanel>
            <TabPanel>
              <NewKBProposalForm />
            </TabPanel>
            <TabPanel>
              <NewKBCallsTab />
            </TabPanel>
            <TabPanel>
              <NewKBCommentsForm />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </ModalBody>
      <ModalFooter>
        {isFormDirty && (
          <Text fontSize="sm" color="orange.500" mr={2}>
            You have unsaved changes
          </Text>
        )}
        <Button mr={3} onClick={onClose}>
          Cancel
        </Button>
        <Button 
          colorScheme="blue" 
          onClick={handleSubmit} 
          isLoading={isLoading}
          isDisabled={validationErrors.length > 0}
        >
          {isEditMode ? 'Update' : 'Create'}
        </Button>
      </ModalFooter>
    </>
  );
};

export default NewKBLeadModal;
